/**
 * googleSheetsAdapter.ts
 *
 * Maps the Google Apps Script Web App API response (from Google Sheets)
 * into MetaLeadGenRow[] arrays for each dashboard section.
 *
 * The Sheets API returns separate flat tables — this adapter normalises them
 * into the existing MetaLeadGenRow type so no UI changes are needed.
 *
 * Data sources:
 *   raw_ads       → kpiRows (KPI cards, daily trend, campaign/ad performance)
 *   age_gender    → ageGenderRows (age & gender breakdown sections)
 *   device        → deviceRows (device breakdown section)
 *   platform      → platformRows (platform breakdown section)
 *   raw_actions   → used to derive leads, purchases, landing page views per ad per day
 */

import type { MetaLeadGenRow } from "../types";

// ─────────────────────────────────────────────
// RAW SHAPES FROM SHEETS API
// ─────────────────────────────────────────────

export interface RawAdRow {
  date_start: string;
  date_stop?: string;
  campaign_name?: string;
  campaign_id?: string;
  adset_name?: string;
  adset_id?: string;
  ad_name?: string;
  ad_id?: string;
  spend?: string | number;
  impressions?: string | number;
  reach?: string | number;
  frequency?: string | number;
  clicks?: string | number;
  inline_link_clicks?: string | number;
  cpm?: string | number;
  cpc?: string | number;
  ctr?: string | number;
  actions_json?: string;
  cost_per_action_type_json?: string;
  thumbnail_url?: string;    // optional: creative thumbnail/preview image URL
}

export interface RawActionRow {
  date_start: string;
  campaign_name?: string;
  campaign_id?: string;
  adset_name?: string;
  adset_id?: string;
  ad_name?: string;
  ad_id?: string;
  action_type?: string;
  value?: string | number;
  cost_per_action?: string | number;
}

export interface AgeGenderRow {
  date_start: string;
  date_stop?: string;
  campaign_name?: string;
  campaign_id?: string;
  adset_name?: string;
  adset_id?: string;
  ad_name?: string;
  ad_id?: string;
  age?: string;
  gender?: string;
  spend?: string | number;
  impressions?: string | number;
  reach?: string | number;
  clicks?: string | number;
  inline_link_clicks?: string | number;
  cpm?: string | number;
  cpc?: string | number;
  ctr?: string | number;
}

export interface DeviceRow {
  date_start: string;
  campaign_name?: string;
  adset_name?: string;
  ad_name?: string;
  impression_device?: string;
  spend?: string | number;
  impressions?: string | number;
  reach?: string | number;
  clicks?: string | number;
  inline_link_clicks?: string | number;
  cpm?: string | number;
  cpc?: string | number;
  ctr?: string | number;
}

export interface PlatformRow {
  date_start: string;
  campaign_name?: string;
  adset_name?: string;
  ad_name?: string;
  publisher_platform?: string;
  platform_position?: string;
  spend?: string | number;
  impressions?: string | number;
  reach?: string | number;
  clicks?: string | number;
  inline_link_clicks?: string | number;
  cpm?: string | number;
  cpc?: string | number;
  ctr?: string | number;
}

export interface SheetsApiResponse {
  raw_ads?: RawAdRow[];
  raw_actions?: RawActionRow[];
  age_gender?: AgeGenderRow[];
  device?: DeviceRow[];
  platform?: PlatformRow[];
}

export interface MetaSheetsAdaptedData {
  /** Primary rows from raw_ads — use for KPIs, daily trend, campaign/ad tables */
  kpiRows: MetaLeadGenRow[];
  /** Age/gender breakdown rows */
  ageGenderRows: MetaLeadGenRow[];
  /** Device breakdown rows */
  deviceRows: MetaLeadGenRow[];
  /** Platform breakdown rows */
  platformRows: MetaLeadGenRow[];
  /** Which lead action_type was selected globally */
  selectedLeadEvent: string;
  /** Which purchase action_type was selected globally */
  selectedPurchaseEvent: string;
  /** Debug info for console logging */
  debugInfo: {
    rawAdsCount: number;
    rawActionsCount: number;
    selectedLeadEvent: string;
    selectedPurchaseEvent: string;
    totalSpend: number;
    totalLeads: number;
    totalPurchases: number;
  };
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

/** Safely parse any value to a finite number. Returns 0 on failure. */
function n(val: string | number | null | undefined): number {
  if (val === null || val === undefined || val === "") return 0;
  const parsed = typeof val === "number" ? val : parseFloat(String(val).replace(/,/g, ""));
  return isFinite(parsed) ? parsed : 0;
}

/** Normalise impression_device strings → MetaLeadGenRow device type */
function normalizeDevice(raw: string | undefined): MetaLeadGenRow["device"] {
  const d = (raw || "").toLowerCase();
  if (d.includes("iphone") || d === "ios") return "ios";
  if (d.includes("ipad"))                  return "ios";
  if (d.includes("android"))               return "android";
  if (d.includes("mobile") || d.includes("phone")) return "android"; // unknown mobile → android as fallback
  return "desktop";
}

/** Normalise publisher_platform strings → MetaLeadGenRow platform type */
function normalizePlatform(raw: string | undefined): MetaLeadGenRow["platform"] {
  switch ((raw || "").toLowerCase()) {
    case "instagram":         return "instagram";
    case "messenger":         return "messenger";
    case "audience_network":  return "audience_network";
    default:                  return "facebook";
  }
}

/** Normalise gender strings */
function normalizeGender(raw: string | undefined): MetaLeadGenRow["gender"] {
  if (raw === "male")   return "male";
  if (raw === "female") return "female";
  return "unknown";
}

/** Safe JSON parse — returns null on failure */
function tryParseJson(s: string | undefined): unknown {
  if (!s || typeof s !== "string") return null;
  try { return JSON.parse(s); } catch { return null; }
}

// ─────────────────────────────────────────────
// LEAD / PURCHASE EVENT DETECTION
// ─────────────────────────────────────────────

const LEAD_PRIORITY = [
  "onsite_web_lead",
  "lead",
  "offsite_conversion.fb_pixel_lead",
  "onsite_conversion.lead_grouped",
  "onsite_conversion.lead",
];

const PURCHASE_PRIORITY = [
  "onsite_conversion.purchase",
  "omni_purchase",
  "onsite_web_purchase",
];

/** Scan all raw_actions rows to pick the highest-priority lead event present */
function detectLeadEvent(actions: RawActionRow[]): string {
  const found = new Set(actions.map(r => (r.action_type || "").trim()).filter(Boolean));
  for (const candidate of LEAD_PRIORITY) {
    if (found.has(candidate)) return candidate;
  }
  return "lead"; // fallback
}

/** Scan all raw_actions rows to pick the highest-priority purchase event present */
function detectPurchaseEvent(actions: RawActionRow[]): string {
  const found = new Set(actions.map(r => (r.action_type || "").trim()).filter(Boolean));
  for (const candidate of PURCHASE_PRIORITY) {
    if (found.has(candidate)) return candidate;
  }
  return "onsite_conversion.purchase"; // fallback
}

/**
 * Build a map: `${date}_${adId}` → count
 * from raw_actions rows matching a specific action_type.
 */
function buildActionMap(
  actions: RawActionRow[],
  targetType: string
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of actions) {
    if ((row.action_type || "").trim() !== targetType) continue;
    const date  = (row.date_start || "").trim().slice(0, 10);
    const adId  = (row.ad_id || "").trim();
    const key   = `${date}_${adId}`;
    map.set(key, (map.get(key) || 0) + n(row.value));
  }
  return map;
}

// ─────────────────────────────────────────────
// MAIN ADAPTER
// ─────────────────────────────────────────────

export function adaptSheetsResponse(raw: SheetsApiResponse): MetaSheetsAdaptedData {
  const rawAds     = Array.isArray(raw.raw_ads)     ? raw.raw_ads     : [];
  const rawActions = Array.isArray(raw.raw_actions) ? raw.raw_actions : [];
  const ageGender  = Array.isArray(raw.age_gender)  ? raw.age_gender  : [];
  const deviceData = Array.isArray(raw.device)      ? raw.device      : [];
  const platformData = Array.isArray(raw.platform)  ? raw.platform    : [];

  // Detect which events to use globally
  const selectedLeadEvent     = detectLeadEvent(rawActions);
  const selectedPurchaseEvent = detectPurchaseEvent(rawActions);

  // Build per-(date, adId) lookup maps
  const leadMap         = buildActionMap(rawActions, selectedLeadEvent);
  const purchaseMap     = buildActionMap(rawActions, selectedPurchaseEvent);
  const lpvMap          = buildActionMap(rawActions, "landing_page_view");

  // ── KPI rows from raw_ads ──────────────────────────────────────────────────
  const kpiRows: MetaLeadGenRow[] = rawAds
    .filter(row => row.date_start)
    .map(row => {
      const date  = (row.date_start || "").trim().slice(0, 10);
      const adId  = (row.ad_id  || "").trim();
      const key   = `${date}_${adId}`;

      const impressions = n(row.impressions);
      const clicks      = Math.max(n(row.inline_link_clicks), n(row.clicks));
      const spend       = n(row.spend);
      const leads       = leadMap.get(key) || 0;

      // Parse actions_json for landing page views (fallback) and video views
      let lpv = lpvMap.get(key) || 0;
      let videoViews = 0;
      const parsedActions = tryParseJson(row.actions_json);
      if (Array.isArray(parsedActions)) {
        const actions = parsedActions as Array<{ action_type?: string; value?: string | number }>;
        if (!lpv) {
          const lpvAction = actions.find(a => a.action_type === "landing_page_view");
          if (lpvAction) lpv = n(lpvAction.value);
        }
        const vvAction = actions.find(a => a.action_type === "video_view");
        if (vvAction) videoViews = n(vvAction.value);
      }

      const purchases = purchaseMap.get(key) || 0;

      return {
        date,
        campaignId:   (row.campaign_id || "").trim(),
        campaignName: (row.campaign_name || "").trim(),
        adsetId:      (row.adset_id || "").trim(),
        adsetName:    (row.adset_name || "").trim(),
        adId,
        adName:       (row.ad_name || "").trim(),
        // No breakdown dimensions in raw_ads
        age:      "all",
        gender:   "unknown",
        device:   "ios",
        platform: "facebook",
        country:  "",
        spend,
        impressions,
        clicks,
        landingPageViews: lpv,
        leads,
        bounces:    Math.max(0, clicks - lpv),
        reach:      n(row.reach) || undefined,
        videoViews:   videoViews || undefined,
        thumbnailUrl: row.thumbnail_url ? String(row.thumbnail_url).trim() : undefined,
        // Store purchases in qualifiedLeads slot for funnel display
        qualifiedLeads: purchases,
      } satisfies MetaLeadGenRow;
    });

  // ── Age/gender rows — leads distributed proportionally from raw_actions ───────
  // Meta's age_gender breakdown doesn't include conversion events. We distribute
  // the leads from leadMap (per date+adId) proportionally by each row's click share
  // within that ad on that day. This is the best approximation available.

  // Pre-build total clicks per date_adId across all age/gender segments
  const ageGenderAdClickTotals = new Map<string, number>();
  for (const row of ageGender) {
    if (!row.date_start) continue;
    const key = `${(row.date_start || "").trim().slice(0, 10)}_${(row.ad_id || "").trim()}`;
    ageGenderAdClickTotals.set(key, (ageGenderAdClickTotals.get(key) ?? 0) + Math.max(n(row.inline_link_clicks), n(row.clicks)));
  }

  const ageGenderRows: MetaLeadGenRow[] = ageGender
    .filter(row => row.date_start)
    .map(row => {
      const date  = (row.date_start || "").trim().slice(0, 10);
      const adId  = (row.ad_id || "").trim();
      const key   = `${date}_${adId}`;
      const clicks = Math.max(n(row.inline_link_clicks), n(row.clicks));
      // Distribute leads proportionally by click share within this ad+day
      const totalLeads  = leadMap.get(key) ?? 0;
      const totalClicks = ageGenderAdClickTotals.get(key) ?? 0;
      const leads = totalClicks > 0 ? Math.round((clicks / totalClicks) * totalLeads) : 0;
      return {
        date,
        campaignId:   (row.campaign_id || "").trim(),
        campaignName: (row.campaign_name || "").trim(),
        adsetId:      (row.adset_id || "").trim(),
        adsetName:    (row.adset_name || "").trim(),
        adId,
        adName:       (row.ad_name || "").trim(),
        age:          (row.age || "unknown").trim(),
        gender:       normalizeGender(row.gender),
        device:       "ios",
        platform:     "facebook",
        country:      "",
        spend:        n(row.spend),
        impressions:  n(row.impressions),
        clicks,
        landingPageViews: 0,
        leads,
        bounces:      0,
        reach:        n(row.reach) || undefined,
      } satisfies MetaLeadGenRow;
    });

  // ── Device rows ────────────────────────────────────────────────────────────
  const deviceRows: MetaLeadGenRow[] = deviceData
    .filter(row => row.date_start)
    .map(row => {
      const clicks = Math.max(n(row.inline_link_clicks), n(row.clicks));
      return {
        date:         (row.date_start || "").trim().slice(0, 10),
        campaignId:   "",
        campaignName: (row.campaign_name || "").trim(),
        adsetId:      "",
        adsetName:    (row.adset_name || "").trim(),
        adId:         "",
        adName:       (row.ad_name || "").trim(),
        age:          "all",
        gender:       "unknown",
        device:       normalizeDevice(row.impression_device),
        platform:     "facebook",
        country:      "",
        spend:        n(row.spend),
        impressions:  n(row.impressions),
        clicks,
        landingPageViews: 0,
        leads:        0,
        bounces:      0,
        reach:        n(row.reach) || undefined,
      } satisfies MetaLeadGenRow;
    });

  // ── Platform rows ──────────────────────────────────────────────────────────
  const platformRows: MetaLeadGenRow[] = platformData
    .filter(row => row.date_start)
    .map(row => {
      const clicks = Math.max(n(row.inline_link_clicks), n(row.clicks));
      return {
        date:         (row.date_start || "").trim().slice(0, 10),
        campaignId:   "",
        campaignName: (row.campaign_name || "").trim(),
        adsetId:      "",
        adsetName:    (row.adset_name || "").trim(),
        adId:         "",
        adName:       (row.ad_name || "").trim(),
        age:          "all",
        gender:       "unknown",
        device:       "ios",
        platform:     normalizePlatform(row.publisher_platform),
        country:      "",
        spend:        n(row.spend),
        impressions:  n(row.impressions),
        clicks,
        landingPageViews: 0,
        leads:        0,
        bounces:      0,
        reach:        n(row.reach) || undefined,
      } satisfies MetaLeadGenRow;
    });

  // ── Debug info ─────────────────────────────────────────────────────────────
  const totalSpend     = kpiRows.reduce((s, r) => s + r.spend, 0);
  const totalLeads     = kpiRows.reduce((s, r) => s + r.leads, 0);
  const totalPurchases = kpiRows.reduce((s, r) => s + (r.qualifiedLeads || 0), 0);

  const debugInfo = {
    rawAdsCount:          rawAds.length,
    rawActionsCount:      rawActions.length,
    selectedLeadEvent,
    selectedPurchaseEvent,
    totalSpend,
    totalLeads,
    totalPurchases,
  };

  return {
    kpiRows,
    ageGenderRows,
    deviceRows,
    platformRows,
    selectedLeadEvent,
    selectedPurchaseEvent,
    debugInfo,
  };
}
